'use client';

/**
 * CallBotHowItWorks — the conversation cycle.
 *
 * 3-step flow rendered with the shared StepCards component.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiCursorArrowRays, HiQrCode, HiSpeakerWave } from 'react-icons/hi2';

type Props = { lang: Locale };

type StepKey = 'scan' | 'tap' | 'reply';

const STEP_NUMS: Record<StepKey, string> = {
  scan: '01',
  tap: '02',
  reply: '03',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  scan: HiQrCode,
  tap: HiCursorArrowRays,
  reply: HiSpeakerWave,
};

const KEYS: StepKey[] = ['scan', 'tap', 'reply'];

const CallBotHowItWorks = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const steps: StepCardItem[] = KEYS.map((k) => ({
    key: k,
    num: STEP_NUMS[k],
    title: t(`CallBot/HowItWorks/${k}/title`),
    description: t(`CallBot/HowItWorks/${k}/body`),
    Icon: STEP_ICONS[k],
  }));

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={t('CallBot/HowItWorks/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('CallBot/HowItWorks/title1')} <span style={{ color: '#B4C1D8' }}>{t('CallBot/HowItWorks/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('CallBot/HowItWorks/subtitle')}
        </p>

        <StepCards steps={steps} accent="#A78BFA" minCardWidth={260} />
      </div>
    </section>
  );
};

export default CallBotHowItWorks;
